import logger from '../logger.js';
import { getSupportedLocales } from './localeState.js';
import NavigationService from '../NavigationService.js';
import { DEFAULT_LOCALE_CODE, LOCALES } from '../constants/locales.js';
// Regex patterns for locale detection - separated by purpose for better maintainability
// Validates locale codes using a subset of BCP 47 specification:
// - Only 2-character language codes (subset of ISO 639-1)
// - Optional 4-character script subtags (ISO 15924) in Title Case
// - Required 2-character region codes (ISO 3166-1) in UPPERCASE only
// - Strict case requirements: lang(lowercase)-Script(Title)-REGION(UPPER)
const LOCALE_CODE_VALIDATION_REGEX = /^[a-z]{2}(-[A-Z][a-z]{3})?-[A-Z]{2}$/;
const LOCALE_PATH_EXTRACTION_REGEX = /^\/([a-z]{2}-[A-Z]{2}|[a-z]{2}-[a-z]{2})(?=\/|$)/;
// =============================================================================
// PURE UTILITY FUNCTIONS (No Side Effects)
// =============================================================================
/**
 * Convert a BCP 47 Locale to a LocalePath for URL usage
 * Removes script subtags and applies case preservation based on source LocalePath context
 * Examples:
 * - localeToLocalePath('sr-Latn-RS', 'en-US') → 'sr-RS' (preserves uppercase from source)
 * - localeToLocalePath('sr-Latn-RS', 'en-us') → 'sr-rs' (preserves lowercase from source)
 * - localeToLocalePath('sr-Latn-RS', 'en-US', 'lowercase') → 'sr-rs' (forces lowercase)
 * - localeToLocalePath('sr-Latn-RS', 'en-us', 'uppercase') → 'sr-RS' (forces uppercase)
 * @param {string} locale - The BCP 47 locale code (e.g., sr-Latn-RS)
 * @param {string} [sourceLocalePath] - Source LocalePath to preserve case pattern from
 * @param {string} [caseFormat] - Override case format: 'preserve', 'lowercase', or 'uppercase'
 * @returns {string} The LocalePath for URL usage
 */
export function localeToLocalePath(locale, sourceLocalePath = null, caseFormat = 'preserve') {
    if (!locale || typeof locale !== 'string') {
        return locale;
    }
    // Use normalizeLocaleCode to remove script subtags (3-part → 2-part conversion)
    const targetLocalePath = normalizeLocaleCode(locale);
    // Apply case format override if specified
    if (caseFormat === 'lowercase') {
        return targetLocalePath.toLowerCase();
    }
    else if (caseFormat === 'uppercase') {
        const parts = targetLocalePath.split('-');
        if (parts.length === 2) {
            return `${parts[0].toLowerCase()}-${parts[1].toUpperCase()}`;
        }
        return targetLocalePath;
    }
    // Default: preserve case pattern from source LocalePath
    if (sourceLocalePath) {
        return applyOriginalCountryCodeCasePattern(sourceLocalePath, targetLocalePath);
    }
    return targetLocalePath;
}
/**
 * Convert a LocalePath from URL to a BCP 47 Locale using supported locales
 * Uses getLocaleVariations to find the best matching supported locale
 * Examples:
 * - localePathToLocale('sr-rs', supportedLocales) → 'sr-Latn-RS' (if sr-Latn-RS is supported)
 * - localePathToLocale('en-us', supportedLocales) → 'en-US' (if en-US is supported)
 * @param {string} localePath - The LocalePath from URL (e.g., sr-rs, en-us)
 * @param {string[]} supportedLocales - Array of supported BCP 47 locale codes
 * @returns {string|null} The matching BCP 47 locale or null if no match found
 */
export function localePathToLocale(localePath, supportedLocales) {
    if (!localePath || typeof localePath !== 'string' || !Array.isArray(supportedLocales)) {
        return null;
    }
    // Normalize the LocalePath to standard format (xx-XX)
    const normalizedPath = convertPathMatchToLocaleString(localePath);
    if (!normalizedPath) {
        return null;
    }
    // First, check for exact match in supported locales
    const exactMatch = supportedLocales.find(locale => normalizedPath.toLowerCase() === localeToLocalePath(locale).toLowerCase());
    if (exactMatch) {
        return exactMatch;
    }
    // Use getLocaleVariations to find all possible locale variations that match this LocalePath
    const variations = getLocaleVariations(normalizedPath);
    // Find the first variation that is in our supported locales list
    for (const variation of variations) {
        if (supportedLocales.includes(variation)) {
            return variation;
        }
    }
    return null; // No matching supported locale found
}
/**
 * Normalize a BCP 47 locale code by removing script subtags
 * Converts BCP 47 3-part locales (language-script-region) to 2-part (language-region)
 * Examples: sr-Latn-RS → sr-RS, zh-Hans-CN → zh-CN, zh-Hant-TW → zh-TW
 * @param {string} localeCode - The locale code to normalize
 * @returns {string} The normalized locale code
 */
export function normalizeLocaleCode(localeCode) {
    if (!localeCode || typeof localeCode !== 'string') {
        return localeCode;
    }
    // Handle 3-part locales with script subtags (language-script-region)
    const parts = localeCode.split('-');
    if (parts.length === 3) {
        const [lang, script, region] = parts;
        // Script subtags are 4 letters, first capitalized, rest lowercase (Latn, Cyrl, Hans, Hant, etc.)
        if (script.length === 4 && script.match(/^[A-Z][a-z]{3}$/)) {
            return `${lang}-${region}`;
        }
    }
    return localeCode;
}
/**
 * Get all possible locale code variations for matching
 * Uses data-driven approach to find locales that match language-region pattern
 * Properly extracts 2-char language and 2-char country codes from xx-Xxxx-XX variants
 * @param {string} localeCode - The base locale code (e.g., "sr-RS", "sr-Latn-RS")
 * @returns {string[]} Array of matching variations from master LOCALES list
 */
export function getLocaleVariations(localeCode) {
    if (!localeCode) {
        return [];
    }
    // Extract 2-char language and 2-char country from input locale
    const inputParts = localeCode.split('-');
    if (inputParts.length < 2) {
        return [];
    }
    // Extract 2-character language code (always first part)
    const inputLang = inputParts[0];
    if (inputLang.length !== 2) {
        return [];
    }
    // Extract 2-character country code (last part, must be 2 chars)
    const inputCountry = inputParts[inputParts.length - 1];
    if (inputCountry.length !== 2) {
        return [];
    }
    // Search through master LOCALES list for variations
    // This is important during initialization when supportedLocales is being built
    const allLocales = LOCALES.map(l => l.code);
    // Find ALL locales in master list that match lang-*-country pattern
    // This handles script subtag variations like sr-RS → [sr-Latn-RS, sr-Cyrl-RS]
    const variations = allLocales.filter(supported => {
        const supportedParts = supported.split('-');
        // Extract 2-character language code (always first part)
        const supportedLang = supportedParts[0];
        if (supportedLang.length !== 2) {
            return false;
        }
        // Extract 2-character country code (last part, must be 2 chars)
        const supportedCountry = supportedParts[supportedParts.length - 1];
        if (supportedCountry.length !== 2) {
            return false;
        }
        const languageMatch = supportedLang.toLowerCase() === inputLang.toLowerCase();
        const countryMatch = supportedCountry.toLowerCase() === inputCountry.toLowerCase();
        const matches = languageMatch && countryMatch;
        return matches;
    });
    return variations;
}
/**
 * Convert a path match to a normalized locale string
 * Handles case normalization and format standardization for locale codes extracted from URLs
 * @param {string} pathMatch - The raw locale string extracted from URL path
 * @returns {string|null} The normalized locale string or null if invalid
 */
export function convertPathMatchToLocaleString(pathMatch) {
    if (!pathMatch || typeof pathMatch !== 'string') {
        return null;
    }
    // Normalize the locale code extracted from the path
    // URLs may have various case patterns, so normalize to standard format
    const parts = pathMatch.split('-');
    if (parts.length === 2) {
        // Standard 2-part locale: language-country
        const [lang, country] = parts;
        return `${lang.toLowerCase()}-${country.toUpperCase()}`;
    }
    // Return as-is if not a standard 2-part locale
    return pathMatch;
}
/**
 * Extract raw locale string from URL pathname
 * Parses the path for the locale code at the start of the path
 * @param {string} pathname - The URL pathname to extract from
 * @returns {string|null} The extracted raw locale string or null if not found
 */
export function extractRawLocaleStringFromPath(pathname) {
    if (!pathname || typeof pathname !== 'string') {
        return null;
    }
    // Match locale pattern at the beginning of the path
    const pathMatch = pathname.match(LOCALE_PATH_EXTRACTION_REGEX);
    if (!pathMatch || pathMatch.length < 2) {
        return null;
    }
    return pathMatch[1];
}
/**
 * Extract locale code from URL pathname
 * Parses the path for the locale code at the start of the path
 * @param {string} pathname - The URL pathname to extract from
 * @returns {string|null} The extracted locale code or null if not found
 */
export function extractLocaleStringFromPath(pathname) {
    const pathMatch = extractRawLocaleStringFromPath(pathname);
    if (!pathMatch) {
        return null;
    }
    return convertPathMatchToLocaleString(pathMatch);
}
/**
 * Apply the country code case pattern from original locale to target locale
 * Language code is always forced to lowercase, only country code case is preserved
 * For URLs, script subtags are also lowercased for consistency
 * Handles both 2-part (lang-country) and 3-part (lang-script-country) locales
 * @param {string} originalLocale - The original locale to copy country case pattern from
 * @param {string} targetLocale - The target locale to apply country case pattern to
 * @returns {string} The target locale with original's country case pattern applied
 */
export function applyOriginalCountryCodeCasePattern(originalLocale, targetLocale) {
    if (!originalLocale || !targetLocale) {
        return targetLocale;
    }
    const origParts = originalLocale.split('-');
    const targetParts = targetLocale.split('-');
    // Get the country code (last part for both 2-part and 3-part locales)
    const origCountry = origParts[origParts.length - 1];
    const targetCountry = targetParts[targetParts.length - 1];
    if (!origCountry || !targetCountry || targetParts.length < 2) {
        return targetLocale;
    }
    // Determine case pattern from original country code
    const newCountry = origCountry === origCountry.toLowerCase()
        ? targetCountry.toLowerCase()
        : targetCountry.toUpperCase();
    // Rebuild the locale with proper casing for URLs
    const newParts = [...targetParts];
    newParts[0] = newParts[0].toLowerCase(); // Language always lowercase
    newParts[newParts.length - 1] = newCountry; // Country preserves original case
    // For 3-part locales, make script part lowercase for URL consistency
    if (newParts.length === 3) {
        newParts[1] = newParts[1].toLowerCase(); // Script part lowercase for URLs
    }
    return newParts.join('-');
}
/**
 * Generate a path with the new target locale path by replacing the old locale in the current path
 * Properly converts BCP 47 Locale to LocalePath with context-aware case preservation
 * @param {string} localeCode - The target BCP 47 locale code (e.g., sr-Latn-RS)
 * @param {string} [pathname] - The pathname to modify (defaults to current NavigationService.getPathname())
 * @returns {string} The generated path with LocalePath
 * @throws {Error} If the locale code format is invalid or if no valid locale pattern is found in the URL path
 */
export function generateLocalePath(localeCode, pathname = NavigationService.getPathname()) {
    if (!localeCode || typeof localeCode !== 'string' || !LOCALE_CODE_VALIDATION_REGEX.test(localeCode)) {
        logger.debug('generateLocalePath: Invalid locale code provided:', localeCode);
        const error = new Error('generateLocalePath: Invalid locale code format');
        error.localeCode = localeCode;
        error.pathname = pathname;
        error.code = 'INVALID_LOCALE_FORMAT';
        throw error;
    }
    // Extract the raw LocalePath from the current pathname for context
    const sourceLocalePath = extractRawLocaleStringFromPath(pathname);
    if (!sourceLocalePath) {
        logger.debug('generateLocalePath: There is no valid locale pattern in the URL path:', pathname);
        const error = new Error('generateLocalePath: No valid locale pattern found in the URL path');
        error.pathname = pathname;
        error.code = 'NO_LOCALE_IN_PATH';
        throw error;
    }
    // Convert BCP 47 Locale to LocalePath using context-aware case preservation
    // This properly handles: sr-Latn-RS + en-US → sr-RS (preserves uppercase)
    // And: sr-Latn-RS + en-us → sr-rs (preserves lowercase)
    const targetLocalePath = localeToLocalePath(localeCode, sourceLocalePath);
    // Replace the locale portion in the path with the new LocalePath
    return pathname.replace(LOCALE_PATH_EXTRACTION_REGEX, `/${targetLocalePath}`);
}
/**
 * Get browser's preferred language
 * @returns {string} The browser language or empty string
 */
export function getBrowserLanguage() {
    return (typeof window !== 'undefined' && window.navigator && window.navigator.language) || '';
}
/**
 * Find locale object by code, with support for variations
 * @param {string} localeCode - The locale code to find
 * @returns {Object|null} The locale object or null if not found
 */
export function findLocaleByCode(localeCode) {
    if (!localeCode || typeof localeCode !== 'string') {
        return null;
    }
    const exactMatch = LOCALES.find(l => l.code === localeCode);
    if (exactMatch) {
        return exactMatch;
    }
    const variantMatches = getLocaleVariations(localeCode);
    for (const variantCode of variantMatches) {
        const variantMatch = LOCALES.find(l => l.code === variantCode);
        if (variantMatch) {
            return variantMatch;
        }
    }
    return null;
}
/**
 * Get the current BCP 47 locale code from URL path or browser language
 * This is the single authoritative implementation - all other modules should use this
 * Returns actual Locales (BCP 47), not LocalePaths (URL conventions)
 * @returns {string} The current BCP 47 locale code or default if not supported
 */
export function getCurrentLocaleCode() {
    const supportedLocales = getSupportedLocales();
    // First try to get LocalePath from URL and convert to Locale
    const pathname = NavigationService.getPathname();
    const pathLocalePath = extractRawLocaleStringFromPath(pathname);
    if (pathLocalePath) {
        // Convert LocalePath to BCP 47 Locale using supported locales
        const pathLocale = localePathToLocale(pathLocalePath, supportedLocales);
        if (pathLocale) {
            return pathLocale;
        }
    }
    // No path locale, check browser language
    const browserLanguage = getBrowserLanguage();
    if (browserLanguage) {
        // Check if the browser language is directly supported
        if (supportedLocales.includes(browserLanguage)) {
            return browserLanguage;
        }
        // Check for variations (e.g., sr-RS -> sr-Latn-RS) that are supported
        const variations = getLocaleVariations(browserLanguage);
        for (const variation of variations) {
            if (supportedLocales.includes(variation)) {
                return variation;
            }
        }
    }
    // No path locale or browser locale, fallback to default
    return DEFAULT_LOCALE_CODE;
}
export { DEFAULT_LOCALE_CODE, LOCALES, LOCALE_CODE_VALIDATION_REGEX, LOCALE_PATH_EXTRACTION_REGEX };
